/**
 * FIFO queue that processes one async task at a time.
 *
 * Modeled on the old layoutSyncMiddleware's `SyncMiddlewareAsyncQueue` (see
 * docs/WORKSPACE_UNIFICATION.md). Used by the controller and per-layout
 * content store so that:
 *
 *   - Mutations land at the server in enqueue order.
 *   - Cross-tab broadcasts and IDB writes happen in `onSuccess` — the wire
 *     state and the cache stay in lockstep with what the server actually
 *     accepted.
 *   - On any failure the queue stops draining and the caller can mark itself
 *     "stale" so further writes are blocked until reload.
 *
 * The queue does NOT track stale state itself — that's a higher-level
 * concern. Callers check stale before pushing.
 */

/**
 * Decision returned by `onError` to control queue behavior on failure.
 *
 *   'drop'  (default) — current behavior: discard the failed task and
 *           everything queued behind it. Used for unrecoverable failures
 *           (server rejection, programming errors) where retry would just
 *           keep failing.
 *
 *   'retry' — leave the failed task at the head of the queue and stop
 *           draining. The next `push` retriggers `drain`, which re-invokes
 *           the head task. Used for transient failures (network offline)
 *           where the user's next action is the natural retry trigger.
 */
export type ErrorPolicy = 'drop' | 'retry'

export interface QueueOptions<T> {
  onSuccess?: (result: T) => void
  onError?: (err: unknown) => ErrorPolicy | void
}

interface QueueItem<T> {
  task: () => Promise<T>
  onSuccess?: (result: T) => void
  onError?: (err: unknown) => ErrorPolicy | void
}

export class SerialQueue {
  private queue: Array<QueueItem<unknown>> = []
  private isProcessing = false

  /**
   * Enqueue a task. The task runs after all previously-enqueued tasks
   * complete successfully. Fire-and-forget — observe results via
   * `onSuccess` / `onError`.
   *
   * `T` is task-specific so each call gets typed onSuccess input without
   * having to type the whole queue.
   */
  push<T>(task: () => Promise<T>, opts: QueueOptions<T> = {}): void {
    this.queue.push({
      task: task as () => Promise<unknown>,
      onSuccess: opts.onSuccess as ((result: unknown) => void) | undefined,
      onError: opts.onError,
    })
    // Defer drain to a microtask so that synchronous follow-up `push` calls
    // in the same tick land in the queue *before* any task body starts
    // executing. Without this, an async task body would run sync up to its
    // first `await`, which can clear caller-side coalescing flags before
    // the caller's later sync calls have a chance to coalesce.
    queueMicrotask(() => {
      void this.drain()
    })
  }

  /**
   * True when a task is currently in flight or there are pending tasks.
   * Used by broadcast/visibility handlers to defer reconciliation until
   * the queue is idle.
   */
  isBusy(): boolean {
    return this.isProcessing || this.queue.length > 0
  }

  /**
   * Number of tasks currently in the queue (includes the in-flight task,
   * if any). Callers use this to enforce caps (e.g., go stale after >N
   * pending mutations during an extended outage).
   */
  size(): number {
    return this.queue.length
  }

  /** Drop all pending tasks. Does not interrupt the in-flight task. */
  clear(): void {
    this.queue = []
  }

  private async drain(): Promise<void> {
    if (this.isProcessing) return
    this.isProcessing = true
    try {
      while (this.queue.length > 0) {
        const item = this.queue[0]
        try {
          const result = await item.task()
          this.queue.shift()
          item.onSuccess?.(result)
        } catch (err) {
          // Hand the failure to the caller. They decide whether to drop
          // (default — discard everything queued behind) or retry (keep
          // the failed task at the head; next `push` re-triggers drain
          // and re-invokes it). Used for transient offline failures.
          const policy = item.onError?.(err) ?? 'drop'
          if (policy === 'retry') {
            // Leave queue intact, stop draining. The head will re-run on
            // the next push.
            return
          }
          this.queue = []
          return
        }
      }
    } finally {
      this.isProcessing = false
    }
  }
}
